import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/Common";
import { api, errMsg } from "@/lib/api";
import { fmtNum } from "@/lib/format";

const Stat = ({ label, value, tone = "" }) => (
  <div className="rounded-lg border border-border bg-white px-4 py-3"><div className={`text-2xl font-extrabold font-mono ${tone}`}>{fmtNum(value)}</div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div></div>
);

const Preview = ({ pv, onApply, busy }) => (
  <div className="mt-4 space-y-3 rounded-lg border border-amber-300 bg-amber-50/50 p-4" data-testid="upload-preview">
    <div className="text-sm font-semibold">Preview · {pv.label}</div>
    {pv.kind === "orders" ? (
      <div className="grid grid-cols-3 gap-2"><Stat label="New" value={pv.added} tone="text-emerald-700" /><Stat label="Updated" value={pv.updated} tone="text-blue-700" /><Stat label="Already there" value={pv.same} /></div>
    ) : (
      <>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4"><Stat label="Tracking IDs in file" value={pv.in_file} /><Stat label="Matched" value={pv.matched} tone="text-blue-700" />
          <Stat label="Will change" value={pv.changed} tone="text-amber-700" /><Stat label="Not found" value={pv.not_found} tone="text-red-600" /></div>
        {!!pv.sample_changes.length && (
          <div className="max-h-56 overflow-auto rounded border border-border bg-white"><table className="data-table"><thead><tr><th>AWB</th><th>Order</th><th>From</th><th>To</th></tr></thead>
            <tbody>{pv.sample_changes.map((c) => <tr key={`${c.order_id}|${c.tracking_id}`}><td className="font-mono text-xs">{c.tracking_id}</td><td className="font-mono text-xs">{c.order_id}</td><td className="text-xs">{c.from}</td><td className="text-xs font-semibold">{c.to}</td></tr>)}</tbody></table></div>
        )}
        {!!pv.missing.length && <div className="text-xs text-muted-foreground break-words" data-testid="preview-missing"><b>Not found:</b> {pv.missing.join(", ")}{pv.not_found > pv.missing.length ? ` … +${pv.not_found - pv.missing.length} more` : ""}</div>}
      </>
    )}
    <Button onClick={onApply} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="upload-apply-btn">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Apply to shipments</Button>
  </div>
);

const useFlow = () => {
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { setPv((await fn()).data); } catch (e) { toast.error(errMsg(e)); setPv(null); } finally { setBusy(false); } };
  const apply = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/shipments/apply/${pv.token}`); toast.success(`Saved: ${data.message}`); setPv(null); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return { pv, busy, run, apply };
};

const FileTab = ({ kind, hint }) => {
  const [file, setFile] = useState(null);
  const f = useFlow();
  const check = () => { const fd = new FormData(); fd.append("file", file); f.run(() => api.post(`/shipments/upload/${kind}`, fd)); };
  return (
    <div className="panel p-4 sm:p-5">
      <p className="mb-3 text-sm text-muted-foreground">{hint}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="file" accept=".xlsx,.xlsm,.csv" className="max-w-sm" onChange={(e) => setFile(e.target.files[0])} data-testid={`upload-${kind}-file`} />
        <Button onClick={check} disabled={!file || f.busy} variant="outline" data-testid={`upload-${kind}-check`}>{f.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Check file</Button>
      </div>
      {f.pv && <Preview pv={f.pv} onApply={f.apply} busy={f.busy} />}
    </div>
  );
};

const PasteTab = () => {
  const [text, setText] = useState("");
  const f = useFlow();
  return (
    <div className="panel p-4 sm:p-5">
      <p className="mb-3 text-sm text-muted-foreground">One line per shipment: tracking ID, status, optional expected date (tab, comma or space separated).</p>
      <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-xs" placeholder={"320789923, Delivered\n50012345678\tOut for delivery\t14 Oct 2026"} data-testid="paste-textarea" />
      <Button className="mt-2" variant="outline" disabled={!text.trim() || f.busy} onClick={() => f.run(() => api.post("/shipments/paste", { text }))} data-testid="paste-check-btn">Check lines</Button>
      {f.pv && <Preview pv={f.pv} onApply={f.apply} busy={f.busy} />}
    </div>
  );
};

export default function ShipmentUpload() {
  return (
    <div className="max-w-5xl">
      <PageHeader title="Upload shipment files" subtitle="Every upload shows a preview first. Closed shipments (Delivered, Cancelled, RTO Received) are never changed." />
      <Tabs defaultValue="orders">
        <TabsList className="mb-3" data-testid="upload-tabs">
          <TabsTrigger value="orders" data-testid="tab-orders">Orders file</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">Courier status file</TabsTrigger>
          <TabsTrigger value="paste" data-testid="tab-paste">Paste</TabsTrigger>
        </TabsList>
        <TabsContent value="orders"><FileTab kind="orders" hint="Order register (.xlsx/.csv) with Order_ID, Order_Date, Tracking_ID, Courier_Name, Ship_Date, Customer_Name, Contact_Number, Product. Re-uploading the same file adds nothing." /></TabsContent>
        <TabsContent value="status"><FileTab kind="status" hint="Courier download (LRN / AWB / Waybill + Status columns) or the old side-by-side MIS sheets. Matched on tracking ID." /></TabsContent>
        <TabsContent value="paste"><PasteTab /></TabsContent>
      </Tabs>
    </div>
  );
}
